import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { withApp } from "../lib/story";
import { navItemAllowed } from "../lib/master";
import { useGuide } from "../lib/guide";
import { ArrowUpRight, Pause, Play, SkipForward, X } from "lucide-react";

// Ordered guided journey. Each step optionally sets an appId, navigates to a route,
// and lists which testids the judge should notice.
const STEPS = [
  { title: "Welcome", body: "You're in the Northstar sample workspace behind YUG360-MIND, an Enterprise Modernization Intelligence System. It does not execute modernization. Simulation is the destination, not this demo. We'll walk the spine in about 90 seconds.", route: "/" },
  { title: "1 / Portfolio", body: "Problem: modernization decisions are fragmented and assumptions keep changing. One tenant · seeded applications · intelligence, not execution. Journey is WHY → DECIDE → DELIVER → PROVE. L1–L4 disclosure shows only what the role needs. Not a 1,000-app control tower.", route: "/", focus: "progressive-disclosure" },
  { title: "1b / Full lifecycle", body: "Closed loop: Understand → Decide → Authorize → Assure → Prove. Deck backbone still maps WHY → ASSESS → DECIDE → DESIGN → PLAN → GOVERN → MONITOR → PROVE. DESIGN and MONITOR are Next.", route: "/lifecycle", appId: "APP-042" },
  { title: "2 / Assessment Inputs", body: "Start with WHY — recorded intent — not which app to migrate. Eight evidence domains stay the source of truth. 7R is not authorized on this page.", route: "/assessment", appId: "APP-042" },
  { title: "3 / Golden application", body: "APP-042 — Legacy Order Management. Business critical, awaiting human authorization.", route: "/cockpit?app=APP-042", appId: "APP-042" },
  { title: "3 / BEFORE", body: "Fragmented and risky: Java 8, manual releases, unknowns and conflicts. We never silently convert Unknown to false.", route: "/cockpit?app=APP-042" },
  { title: "4 / DECISION", body: "Recommended strategy is Refactor / Rearchitect. 7R classifies competing hypotheses. Confidence is evidence coverage, unknowns, conflicts and freshness — not an unexplained AI percentage. Suitability ≠ Eligibility ≠ Confidence ≠ Authorization.", route: "/cockpit?app=APP-042" },
  { title: "5 / AFTER", body: "Target TCO, expected savings and payback are the promised outcome — connected back to WHY.", route: "/cockpit?app=APP-042" },
  { title: "6 / Evidence Fabric", body: "No recommendation without evidence. Manual, CSV, JSON and documents work now. Live connectors are Next. Coming soon.", route: "/evidence" },
  { title: "7 / Adaptive questionnaire", body: "Facts that already came from imported evidence are not re-asked. Only open gaps remain.", route: "/evidence" },
  { title: "8 / Decision Intelligence", body: "WHY, 7C/7S and dependencies rank competing hypotheses, then classify them as 7R. Click a row for supporting, contradictory and unknown evidence.", route: "/decision", align: "left" },
  { title: "9 / Human authorization", body: "Only a named human authority can seal the MDR (decision memory). AI cannot authorize. The MDR then becomes immutable with a SHA-256 hash.", route: "/decision", align: "left" },
  { title: "10 / Material change → MDR v2", body: "If a material change happens, a successor MDR is created — v1 is preserved and marked superseded. History is never rewritten. Automatic validity events are Next.", route: "/decision", align: "left" },
  { title: "11 / Human governance", body: "Jira tracks work. YUG360-MIND shows modernization health (GREEN / AMBER / RED) from MDR, wave readiness and gates — not a slip probability. AI cannot pass a gate.", route: "/governance" },
  { title: "12 / Prove value", body: "YUG360-MIND does not deploy customer workloads. It consumes cutover and outcome evidence. Deployment is an event. G8 proves the original WHY.", route: "/delivery" },
  { title: "13 / Dependency impact", body: "Click any dependency node to see which applications, waves and gates it affects. The graph supports decisions — it isn't decoration.", route: "/delivery?focus=dependencies", align: "left" },
  { title: "14 / Value · expected vs actual", body: "Baseline TCO → Transformation investment → Target TCO → Expected savings/ROI → Actual (recorded after G8). Value connects back to the original WHY.", route: "/delivery?focus=value" },
  { title: "15 / Enterprise fit", body: "Five logical layers as intent. Tenant technology strategy (Hosting → Delivery) constrains Replatform vs Refactor. YUG360-MIND sits above CMDB, GitHub, Jira, ADO, ServiceNow, Confluence, CI/CD, Observability and FinOps. It does not execute in those systems.", route: "/enterprise" },
  { title: "Complete", body: "You've seen the spine: WHY → evidence → hypotheses classified as 7R → human MDR → G1–G8 → proved value. An Enterprise Modernization Intelligence System — not another migration factory.", route: "/" },
];

const AUTO_MS = 5200;

export default function Tour({ onClose, onSelectApp, appId }) {
  const nav = useNavigate();
  const guide = useGuide();
  const steps = useMemo(
    () => STEPS.filter((item) => !item.route || navItemAllowed(item.route, guide)),
    [guide],
  );
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);

  const onSelectAppRef = useRef(onSelectApp);
  onSelectAppRef.current = onSelectApp;
  const appIdRef = useRef(appId);
  appIdRef.current = appId;

  const cur = steps[Math.min(step, steps.length - 1)] || STEPS[0];

  useEffect(() => {
    if (step >= steps.length) setStep(0);
  }, [step, steps.length]);

  useEffect(() => {
    const targetApp = cur.appId || appIdRef.current;
    if (cur.route && navItemAllowed(cur.route, guide)) nav(withApp(cur.route, targetApp));
    if (cur.appId && onSelectAppRef.current) onSelectAppRef.current(cur.appId);
  }, [step, cur.route, cur.appId, cur, nav, guide]);

  useEffect(() => {
    if (!playing) return undefined;
    if (step >= steps.length - 1) return undefined;
    const t = setTimeout(() => setStep((s) => Math.min(s + 1, steps.length - 1)), AUTO_MS);
    return () => clearTimeout(t);
  }, [step, playing, steps.length]);

  const progress = useMemo(() => ((step + 1) / Math.max(steps.length, 1)) * 100, [step, steps.length]);

  return (
    <div className={`tour-overlay ${cur.align === "left" ? "tour-left" : ""}`} data-testid="guided-tour">
      <div className="tour-card">
        <button className="tour-close" data-testid="tour-close" onClick={onClose}><X size={16} /></button>
        <div className="tour-progress"><span style={{ width: `${progress}%` }} /></div>
        <p className="kicker">GUIDED TOUR · {step + 1} OF {steps.length}</p>
        <h3>{cur.title}</h3>
        <p className="tour-body">{cur.body}</p>
        <div className="tour-actions">
          <button data-testid="tour-play-toggle" className="text-button" onClick={() => setPlaying(!playing)}>
            {playing ? <><Pause size={13} /> Pause</> : <><Play size={13} /> Play</>}
          </button>
          <div className="tour-nav">
            <button data-testid="tour-back" className="text-button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>Back</button>
            {step < steps.length - 1 ? (
              <button data-testid="tour-next" className="primary-button small-btn" onClick={() => setStep(step + 1)}>Next <SkipForward size={13} /></button>
            ) : (
              <button data-testid="tour-finish" className="primary-button small-btn" onClick={onClose}>Finish <ArrowUpRight size={13} /></button>
            )}
          </div>
        </div>
        <div className="tour-dots">
          {steps.map((_, i) => <span key={i} className={i === step ? "on" : i < step ? "done" : ""} onClick={() => setStep(i)} />)}
        </div>
      </div>
    </div>
  );
}
